/**
 * knowledge/confidence.ts — the confidence model + mandatory explanations.
 *
 * Pure functions of (record, now): confidence is DERIVED every read from the
 * record's evidence, verification status, and freshness — never a stored
 * number that drifts.
 */

import type {
  KnowledgeConfidence,
  KnowledgeExplanation,
  KnowledgeRecord,
  KnowledgeSource,
} from './models';

const DAY = 24 * 60 * 60 * 1000;
/** Freshness half-life: unvalidated knowledge loses half its freshness in 180 days. */
const FRESHNESS_HALF_LIFE_MS = 180 * DAY;

function saturate(count: number, K: number): number {
  return count / (count + K);
}

export function computeConfidence(record: KnowledgeRecord, now: number): KnowledgeConfidence {
  const verifiedCommunityCount = record.evidence.reduce((sum, e) => sum + (e.verifiedCount ?? 0), 0);
  const sources = [...new Set<KnowledgeSource>([record.source, ...record.evidence.map((e) => e.source)])];
  const age = Math.max(0, now - record.lastValidatedAt);
  const freshness = Math.pow(0.5, age / FRESHNESS_HALF_LIFE_MS);
  const providerReliability = record.evidence.length > 0
    ? record.evidence.reduce((sum, e) => sum + e.providerReliability, 0) / record.evidence.length
    : 0.5;

  const verificationFactor =
    record.verification === 'verified' ? 1
    : record.verification === 'unverified' ? 0.75
    : record.verification === 'stale' ? 0.5
    : 0.35; // disputed

  const score =
    (saturate(record.evidence.length, 4) * 30 +      // supporting evidence volume
      saturate(verifiedCommunityCount, 10) * 25 +    // verified community backing
      freshness * 20 +                               // freshness
      providerReliability * 15 +                     // provider reliability
      saturate(sources.length, 3) * 10) *            // source diversity
    verificationFactor;

  return {
    score: Math.max(0, Math.min(100, Math.round(score))),
    verifiedCommunityCount,
    supportingDataSources: sources,
    freshness: Math.round(freshness * 100) / 100,
    providerReliability: Math.round(providerReliability * 100) / 100,
    lastValidatedAt: record.lastValidatedAt,
  };
}

const SOURCE_LABELS: Record<KnowledgeSource, string> = {
  intelligenceEngine: 'the Roamer Intelligence Engine',
  learningEngine: 'the Roamer Learning Engine',
  communityEngine: 'the Roamer Community Intelligence Engine',
  missionEngine: 'the Roamer Mission Engine',
  scoreEngine: 'the Roamer Score Engine',
  roamerLocal: 'Roamer Local',
  verifiedContribution: 'verified community contributions',
  userPreference: 'your saved preferences',
  missionHistory: 'completed mission history',
  travelHistory: 'travel history',
  roamerReport: 'a Roamer Report',
  externalSummary: 'summarized external data',
};

function daysAgo(at: number, now: number): string {
  const days = Math.floor((now - at) / DAY);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

export function buildExplanation(
  record: KnowledgeRecord,
  confidence: KnowledgeConfidence,
  now: number,
): KnowledgeExplanation {
  const level = confidence.score >= 75 ? 'high' : confidence.score >= 45 ? 'moderate' : 'low';
  return {
    whyThisExists: record.evidence.length > 0
      ? `Accumulated from ${record.evidence.length} piece${record.evidence.length === 1 ? '' : 's'} of supporting evidence across ${confidence.supportingDataSources.length} source${confidence.supportingDataSources.length === 1 ? '' : 's'}.`
      : 'Recorded for future reference; supporting evidence has not yet accumulated.',
    whereItOriginated: `Originated from ${SOURCE_LABELS[record.source]}.`,
    whenLastVerified: `Last validated ${daysAgo(record.lastValidatedAt, now)} (status: ${record.verification}).`,
    howConfident: `${level === 'high' ? 'High' : level === 'moderate' ? 'Moderate' : 'Low'} confidence (${confidence.score}/100), backed by ${confidence.verifiedCommunityCount} verified community member${confidence.verifiedCommunityCount === 1 ? '' : 's'}.`,
  };
}
